import React, { useState } from 'react';
import { FiEdit2, FiTrash2, FiCopy, FiClock, FiUsers, FiTrendingUp, FiCheckCircle, FiXCircle } from 'react-icons/fi';

const QuestionCard = ({
  question,
  isSelected = false,
  onSelect = null,
  onUpdate = null,
  onDelete = null,
  onStatusChange = null,
  showActions = true,
  compact = false,
  concise = false
}) => {
  const [showFullQuestion, setShowFullQuestion] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState(question.question?.text || '');
  const [editedOptions, setEditedOptions] = useState(
    (question.options || []).map((o) => o.text || String(o))
  );
  const [editedCorrect, setEditedCorrect] = useState(
    typeof question.correctAnswer === 'number' ? question.correctAnswer : 0
  );

  // Handle selection
  const handleSelect = () => {
    if (onSelect) {
      onSelect(question._id, !isSelected);
    }
  };

  // Handle edit save
  const handleEditSave = () => {
    if (!onUpdate) { setIsEditing(false); return; }
    const payload = { 
      question: { ...(question.question || {}), text: editedQuestion.trim() },
      options: editedOptions.map((t, idx) => ({ label: String.fromCharCode(65 + idx), text: String(t).trim() })),
      correctAnswer: editedCorrect
    };
    onUpdate(question._id, payload);
    setIsEditing(false);
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditedQuestion(question.question?.text || '');
    setEditedOptions((question.options || []).map((o) => o.text || String(o)));
    setEditedCorrect(typeof question.correctAnswer === 'number' ? question.correctAnswer : 0);
    setIsEditing(false);
  };

  // Handle delete
  const handleDelete = () => {
    if (onDelete) {
      onDelete(question._id);
    }
  };

  // Handle copy question
  const handleCopy = async () => {
    try {
      const questionData = {
        question: question.question?.text || '',
        options: question.options,
        correctAnswer: question.correctAnswer,
        explanation: question.explanation?.text || ''
      };
      
      await navigator.clipboard.writeText(JSON.stringify(questionData, null, 2));
      // You might want to show a toast notification here
    } catch (err) {
      console.error('Failed to copy question:', err);
    }
  };

  const difficultyClasses = {
    easy: 'bg-emerald-100 text-emerald-800',
    medium: 'bg-amber-100 text-amber-800',
    hard: 'bg-red-100 text-red-800'
  };
  const statusClasses = {
    active: 'bg-emerald-100 text-emerald-800',
    inactive: 'bg-slate-100 text-slate-600',
    draft: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
    review: 'bg-amber-100 text-amber-800',
    archived: 'bg-red-100 text-red-800'
  };
  const badgeBase = 'inline-flex px-2 py-0.5 rounded text-xs font-medium';
  const outlineBadge = 'border border-slate-200 bg-white text-slate-700';

  // Truncate text
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    // Handle case where text is an object (like option objects)
    if (typeof text === 'object') {
      // If it's an option object, use the text property
      if (text.text) {
        text = text.text;
      } else {
        // Convert object to string as fallback
        text = String(text);
      }
    }
    // Ensure text is a string
    text = String(text);
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={`relative rounded-lg border bg-white shadow-sm transition-colors ${concise ? 'p-4 border-slate-200' : 'p-5 border-slate-200'} ${isSelected ? 'ring-2 ring-slate-400 bg-slate-50/60 border-slate-300' : ''}`}>
      {onSelect && (
        <div className="absolute top-4 left-4 z-[1]">
          <input type="checkbox" checked={isSelected} onChange={handleSelect} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400 h-4 w-4" />
        </div>
      )}

      <div className={`flex justify-between items-start gap-2 mb-3 ${onSelect ? 'pl-8' : ''}`}>
        <div className="flex flex-wrap gap-1.5 min-w-0">
          <span className={`${badgeBase} ${difficultyClasses[question.difficulty?.toLowerCase()] || 'bg-slate-100 text-slate-600'}`}>{question.difficulty || 'Unknown'}</span>
          <span className={`${badgeBase} ${statusClasses[question.status?.toLowerCase()] || 'bg-slate-100 text-slate-600'}`}>{question.status || 'Active'}</span>
          {question.subject && <span className={`${badgeBase} ${outlineBadge}`}>{question.subject}</span>}
          {question.examType && <span className={`${badgeBase} ${outlineBadge}`}>{question.examType}</span>}
        </div>
        {showActions && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button type="button" onClick={handleCopy} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Copy question"><FiCopy className="h-4 w-4" /></button>
            {onUpdate && <button type="button" onClick={() => setIsEditing(true)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Edit question"><FiEdit2 className="h-4 w-4" /></button>}
            {onStatusChange && question.status !== 'approved' && <button type="button" onClick={() => onStatusChange(question._id, 'approved')} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Mark as vetted"><FiCheckCircle className="h-4 w-4" /></button>}
            {onStatusChange && question.status === 'approved' && <button type="button" onClick={() => onStatusChange(question._id, 'review')} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Unvet"><FiXCircle className="h-4 w-4" /></button>}
            {onDelete && <button type="button" onClick={handleDelete} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete question"><FiTrash2 className="h-4 w-4" /></button>}
          </div>
        )}
      </div>

      <div className="mt-2">
        {isEditing ? (
          <div className="space-y-3">
            <textarea value={editedQuestion} onChange={(e) => setEditedQuestion(e.target.value)} rows={4} placeholder="Enter question text..." className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 resize-y" />
            <div>
              <h4 className="text-xs font-medium text-slate-500 mb-2">Edit options</h4>
              <div className="space-y-2">
                {editedOptions.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-600 w-5">{String.fromCharCode(65 + idx)}.</span>
                    <input type="text" value={opt} onChange={(e) => { const next = [...editedOptions]; next[idx] = e.target.value; setEditedOptions(next); }} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30" />
                    <input type="radio" name={`correct-${question._id}`} checked={editedCorrect === idx} onChange={() => setEditedCorrect(idx)} title="Mark correct" className="text-slate-700 focus:ring-slate-400" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={handleEditCancel} className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="button" onClick={handleEditSave} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors">Save</button>
            </div>
          </div>
        ) : (
          <>
            <p className={`text-slate-800 ${concise ? 'text-[0.9375rem] leading-snug' : 'text-base'} mb-1`}>
              {showFullQuestion || compact ? (question.question?.text || 'No question text available') : truncateText(question.question?.text || 'No question text available')}
            </p>
            {!compact && question.question?.text && question.question.text.length > 150 && (
              <button type="button" onClick={() => setShowFullQuestion(!showFullQuestion)} className="text-sm text-slate-500 underline hover:text-slate-700">{showFullQuestion ? 'Show less' : 'Show more'}</button>
            )}
          </>
        )}

        {!compact && question.options && question.options.length > 0 && (
          <div className="mt-3 p-3 rounded-lg border border-slate-200 bg-slate-50/80">
            <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Options</h4>
            <ul className="space-y-1">
              {question.options.map((option, index) => (
                <li key={index} className={`flex items-start gap-2 py-1 ${question.correctAnswer === index ? 'rounded bg-emerald-50 px-2 -mx-2' : ''}`}>
                  <span className={`text-sm font-medium shrink-0 ${question.correctAnswer === index ? 'text-emerald-700' : 'text-slate-600'}`}>{String.fromCharCode(65 + index)}.</span>
                  <span className="text-sm text-slate-700">{truncateText(option.text || option, 80)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!compact && question.explanation?.text && (
          <div className="mt-3 pl-3 border-l-2 border-slate-300 bg-slate-50/50 rounded py-2">
            <h4 className="text-xs font-medium text-slate-500 mb-1">Explanation</h4>
            <p className="text-sm text-slate-600">{truncateText(question.explanation.text, 150)}</p>
          </div>
        )}

        {question.topics && question.topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {question.topics.slice(0, 3).map((topic, index) => (
              <span key={index} className={`${badgeBase} bg-slate-100 text-slate-600`}>{topic}</span>
            ))}
            {question.topics.length > 3 && <span className={`${badgeBase} bg-slate-100 text-slate-600`}>+{question.topics.length - 3}</span>}
          </div>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-4 text-xs text-slate-500">
          {question.timesUsed !== undefined && <span className="inline-flex items-center gap-1"><FiUsers className="h-3.5 w-3.5 opacity-70" />{question.timesUsed} uses</span>}
          {question.averageTime && <span className="inline-flex items-center gap-1"><FiClock className="h-3.5 w-3.5 opacity-70" />{Math.round(question.averageTime)}s avg</span>}
          {question.qualityScore !== undefined && <span className="inline-flex items-center gap-1"><FiTrendingUp className="h-3.5 w-3.5 opacity-70" />{Math.round(question.qualityScore * 100)}% quality</span>}
        </div>
        <div className="flex flex-col items-end gap-0.5 text-xs text-slate-500">
          {question.createdBy && <span>By {question.createdBy.name || question.createdBy.email}</span>}
          {question.createdAt && <span>{formatDate(question.createdAt)}</span>}
          {question.status === 'approved' && <span className="inline-flex items-center gap-1 text-emerald-600"><FiCheckCircle className="h-3.5 w-3.5" /> Vetted</span>}
        </div>
      </div>

      {question.aiAnalysis && (
        <div className="mt-3 p-3 rounded-lg border border-slate-200 bg-slate-50/80 space-y-2">
          {question.aiAnalysis.clarity != null && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500 w-14">Clarity</span>
              <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-slate-500 rounded-full transition-all" style={{ width: `${Number(question.aiAnalysis.clarity) * 100}%` }} />
              </div>
            </div>
          )}
          {question.aiAnalysis.difficulty && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">AI difficulty</span>
              <span className={`${badgeBase} ${difficultyClasses[String(question.aiAnalysis.difficulty).toLowerCase()] || 'bg-slate-100 text-slate-600'}`}>{question.aiAnalysis.difficulty}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
